import { ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import { Badge, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { formatDistanceToNow } from 'date-fns';
import { TournamentTicketStatus } from '@repo/common-types';

interface TournamentTicket {
  id: string;
  displayName: string;
  status: TournamentTicketStatus;
  value: number | null;
  userId: string | null;
  user?: {
    id: string;
    alias: string;
  } | null;
  tournament?: {
    id: string;
    name: string;
  } | null;
  createdAt: Date;
  expiresAt: Date | null;
  usedAt: Date | null;
  used: boolean;
  tournamentRegistrationId: string | null;
}

interface ColumnActionsProps {
  onDelete: (ticketId: string) => void;
  onRegister: (ticketId: string) => void;
  onUnregister: (ticketId: string) => void;
  onUnassign: (ticketId: string) => void;
  onOpenReassignModal: (ticketId: string, currentUserId: string) => void;
  onOpenAssignModal: (ticketId: string) => void;
}

const getStatusBadge = (status: TournamentTicketStatus) => {
  const statusColors: Record<TournamentTicketStatus, string> = {
    [TournamentTicketStatus.PENDING]: 'warning',
    [TournamentTicketStatus.AVAILABLE]: 'success',
    [TournamentTicketStatus.REDEEMED]: 'secondary',
  };

  return (
    <Badge color={statusColors[status] || 'secondary'} className="text-uppercase">
      {status}
    </Badge>
  );
};

const formatDate = (date: Date | null) => {
  if (!date) return '-';
  return new Date(date).toLocaleString();
};

const formatRelativeDate = (date: Date | null) => {
  if (!date) return '-';
  try {
    return formatDistanceToNow(new Date(date), { addSuffix: true });
  } catch {
    return formatDate(date);
  }
};

export const createColumns = (actions: ColumnActionsProps): ColumnDef<TournamentTicket>[] => [
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => getStatusBadge(row.original.status),
    enableSorting: true,
  },
  {
    accessorKey: 'tournament.name',
    header: 'Tournament',
    cell: ({ row }) => {
      const tournament = row.original.tournament;
      if (!tournament) return <span className="text-muted">-</span>;
      return (
        <Link to={`/tournaments/details/${tournament.id}`} className="text-decoration-none">
          {tournament.name}
        </Link>
      );
    },
    enableSorting: false,
  },
  {
    accessorKey: 'value',
    header: 'USDT',
    cell: ({ row }) => {
      const value = row.original.value;
      if (value === null) return <span className="text-muted">-</span>;
      return <span className="fw-medium">${value.toFixed(2)}</span>;
    },
    enableSorting: true,
  },
  {
    accessorKey: 'createdAt',
    header: 'Created At',
    cell: ({ row }) => formatDate(row.original.createdAt),
    enableSorting: true,
    minSize: 180,
  },
  {
    accessorKey: 'expiresAt',
    header: 'Expires At',
    cell: ({ row }) => {
      const expiresAt = row.original.expiresAt;
      if (!expiresAt) return <span className="text-muted">Never</span>;

      const isExpired = new Date(expiresAt) < new Date();
      return <span className={isExpired ? 'text-danger' : ''}>{formatRelativeDate(expiresAt)}</span>;
    },
    enableSorting: true,
    minSize: 140,
  },
  {
    accessorKey: 'usedAt',
    header: 'Used At',
    cell: ({ row }) => {
      const usedAt = row.original.usedAt;
      if (!usedAt) return <span className="text-muted">Not used</span>;
      return formatDate(usedAt);
    },
    enableSorting: true,
    minSize: 180,
  },
  {
    accessorKey: 'user.alias',
    header: 'Assigned Player',
    cell: ({ row }) => {
      const user = row.original.user;
      if (!user) return <Badge color="light">Unassigned</Badge>;
      return (
        <Link to={`/user/edit/${user.id}`} className="text-decoration-none">
          {user.alias}
        </Link>
      );
    },
    enableSorting: false,
  },
  {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => {
      const ticket = row.original;
      const isRegistered = !!ticket.tournamentRegistrationId;
      const hasUser = !!ticket.userId;
      const hasTournament = !!ticket.tournament;
      const isAvailable = ticket.status === TournamentTicketStatus.AVAILABLE;
      const isRedeemed = ticket.status === TournamentTicketStatus.REDEEMED;

      return (
        <UncontrolledDropdown>
          <DropdownToggle tag="button" className="btn btn-sm btn-light">
            <i className="ri-more-2-fill"></i>
          </DropdownToggle>
          <DropdownMenu end>
            {hasTournament && !isRegistered && hasUser && isAvailable && (
              <DropdownItem onClick={() => actions.onRegister(ticket.id)}>
                <i className="ri-login-box-line me-2"></i>
                Register
              </DropdownItem>
            )}
            {isRegistered && (
              <DropdownItem onClick={() => actions.onUnregister(ticket.id)}>
                <i className="ri-logout-box-line me-2"></i>
                Unregister
              </DropdownItem>
            )}
            {!isRedeemed && hasUser && (
              <>
                <DropdownItem onClick={() => actions.onUnassign(ticket.id)}>
                  <i className="ri-user-unfollow-line me-2"></i>
                  Unassign
                </DropdownItem>
                <DropdownItem onClick={() => actions.onOpenReassignModal(ticket.id, ticket.userId!)}>
                  <i className="ri-user-follow-line me-2"></i>
                  Reassign
                </DropdownItem>
              </>
            )}
            {!isRedeemed && !hasUser && (
              <DropdownItem onClick={() => actions.onOpenAssignModal(ticket.id)}>
                <i className="ri-user-add-line me-2"></i>
                Assign to User
              </DropdownItem>
            )}
            {!isRedeemed && (
              <>
                <DropdownItem divider />
                <DropdownItem onClick={() => actions.onDelete(ticket.id)} className="text-danger">
                  <i className="ri-delete-bin-line me-2"></i>
                  Delete
                </DropdownItem>
              </>
            )}
          </DropdownMenu>
        </UncontrolledDropdown>
      );
    },
    enableSorting: false,
  },
];
